import React, { useMemo } from 'react';
import theme from './OptionGroupAccordion.module.scss';
import { useQuery } from '@tanstack/react-query';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { DirectOptionGroupDto, ResidentialUnitExtraData } from '@/types';
import { Badge } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { groupResidentialUnits } from '@/utils/residential-units';
import { getPillVariant } from './OptionGroupAccordion.helpers';
import { UnitStatusPills } from './UnitStatusPills';

export interface OptionGroupUnitsListProps {
    optionGroup: DirectOptionGroupDto;
}

function useResidentialUnitMap(projectId: number) {
    const { data: units } = useQuery({
        queryKey: ['residentialUnits', projectId],
        queryFn: () => getResidentialUnitsByProjectId(projectId),
    });

    return useMemo(() => {
        if (!units) {
            return null;
        }

        const map = new Map<number, ResidentialUnitExtraData>();
        units.forEach((item) => map.set(item.id, item));
        return map;
    }, [units]);
}

const UnitCutoffCount = 4;
export const OptionGroupUnitsList: React.FC<OptionGroupUnitsListProps> = ({ optionGroup }) => {
    const allUnits = useResidentialUnitMap(optionGroup.projectId);
    const groups = useMemo(() => {
        if (!allUnits) {
            return [];
        }

        const a = optionGroup.linkedUnits
            .map((x) => ({
                unit: allUnits.get(x.residentialUnitId)!,
                link: x,
            }))
            .filter((x) => x.unit != null);

        return groupResidentialUnits(a, (x) => x.unit);

        // return Object.values(
        //     groupBy(
        //         optionGroup.linkedUnits.map((x) => allUnits.get(x.residentialUnitId)).filter(notEmptyPredicate),
        //         (x) => x.residentialUnitGroupId
        //     )
        // );
    }, [allUnits, optionGroup]);

    return (
        <div className={theme.unitsList}>
            {groups.map((g) => (
                <React.Fragment key={g.id ?? -1}>
                    <span className={theme.unitsListLabel}>{g.name || t`Ungrouped`}:</span>
                    <div className={theme.unitsListPills}>
                        {g.units.length >= UnitCutoffCount ? (
                            <UnitStatusPills group={g} />
                        ) : (
                            g.units.map((u) => (
                                <Badge key={u.unit.id} size="sm" variant={getPillVariant(u.link.status)} appearance="subtle">
                                    <Trans>{u.unit.name}</Trans>
                                </Badge>
                            ))
                        )}
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};
